import React, {CSSProperties, useEffect, useRef, useState} from 'react'
import {CheckboxInput, Fieldset, IconButton, NumberInput, Text} from "atmosfair-ui";
import DatePicker from "react-datepicker"
import {useTranslation} from "react-i18next";
import "./styles.css"
import {DateTime} from "luxon";
import {cloneDeep} from "lodash";
import {VALIDATE_FORM_STARTING_NUMBER} from "../../constants/default-values";
import {useInView} from "react-intersection-observer";
import clsx from 'clsx';
import {getDaysCountBetweenDates} from "../../utils/common";
import {toast} from "react-toastify";
import ToastWithTextOnly from "../../molecules/toasts/toast-with-text-only/ToastWithTextOnly";
import {Tooltip} from "@mui/material";
import {DaysRange, getDatePattern} from "../activity/hotel/choose-hotel/utils";
import {setDateFormat} from '../../utils/setter/common';
import useOutsideClick from "../../hooks/use-outside-click";
import {formatDateString} from "../../utils/string/common";

export const DATE_RANGE_PICKER_START_USE_DATES = false
export const DATE_RANGE_PICKER_START_COUNT = 1
export const DATE_RANGE_PICKER_START_DATE = DateTime.now().toLocal()
export const DATE_RANGE_PICKER_END_DATE = DateTime.now().plus({days: DATE_RANGE_PICKER_START_COUNT}).toLocal()

export interface DateRangePickerProps {
    minNumberNightsOrDays: number,
    maxNumberNightsOrDays: number,
    numberInputContainerStyle?: CSSProperties,
    numberInputAriaErrorMessage?: string,
    dateRange?: DaysRange,
    setDateRange?: (dateRange: DaysRange, nightsDaysCount: number) => void,
    nightsDaysCount?: number,
    setNightsDaysCount?: (useDates: boolean, value: number, dateRange: DaysRange) => void,
    useDates?: boolean,
    setUseDates?: (value: boolean, dateRange: DaysRange, nightsDaysCount: number) => void
    containerClasses?: string | string[],
    validateForm?: number,
    onFormIsValid?: (useDates: boolean, daysCount: number, dateRange?: DaysRange) => void
    isNights: boolean,
    showOnlyCalendarIcon?: boolean,
    showOnlyCalendar?: boolean,
    showMaxHotelDurationExceededToast?: boolean,
    setCalendarOpen?: (open: boolean) => void,
    minDate?: DateTime
    maxDate?: DateTime,
    openToDate?: DateTime,
    openCalendarImmediately?: boolean,
    disabled?: boolean,
    disableCalendar?: boolean,
    disableCalendarTooltip?: string,
    containerStyles?: CSSProperties,
    numberCheckboxWrapperClasses?: string | string[]
}

// Maybe use this https://codesandbox.io/p/sandbox/festive-glitter-8sb7l0?file=%2Fsrc%2FApp.js
// to detect if calendar is off screen and position it better
// also add closeOnClickOutside to calendar
// it to conform with all other forms (setIsFormValid in useEffect Hook) -> do not pass every prop change with setDateRange
// and/or setDayCount to parent -> causes re-render of entire parent component which is not needed . Better to onlu request
// data once like done in all other forms in FE. Might clash with journey creation because we need the number of days straight away
const DateRangePicker = ({
                             minNumberNightsOrDays,
                             maxNumberNightsOrDays,
                             numberInputContainerStyle,
                             numberInputAriaErrorMessage,
                             isNights,
                             dateRange,
                             setDateRange,
                             nightsDaysCount,
                             setNightsDaysCount,
                             useDates,
                             setUseDates,
                             containerClasses,
                             validateForm,
                             onFormIsValid,
                             showOnlyCalendarIcon,
                             showMaxHotelDurationExceededToast,
                             setCalendarOpen,
                             minDate,
                             maxDate,
                             openToDate,
                             openCalendarImmediately,
                             showOnlyCalendar,
                             disabled,
                             disableCalendar,
                             disableCalendarTooltip,
                             containerStyles,
                             numberCheckboxWrapperClasses,
                         }: DateRangePickerProps) => {
    const [useExactDates, setUseExactDates] = useState(useDates || DATE_RANGE_PICKER_START_USE_DATES)
    const [count, setCount] = useState(nightsDaysCount ?? DATE_RANGE_PICKER_START_COUNT)
    const [startDate, setStartDate] = useState<Date>(openToDate ? openToDate.toJSDate() : new Date());
    const [endDate, setEndDate] = useState<Date | null>(dateRange ? dateRange.endDate.toJSDate() : null)
    const [numberOfNightsOrDaysError, setNumberOfNightsOrDaysError] = useState(false)
    const [openCalendar, setOpenCalendar] = useState(Boolean(openCalendarImmediately))
    const calendarRef = useRef<HTMLDivElement>(null)
    const iconButtonRef = useRef<HTMLDivElement>(null)
    const {t, i18n} = useTranslation()
    const {language} = i18n
    const {resolvedLanguage} = i18n
    const languageForDatePattern = language === 'en' ? 'en-GB' : language
    const localeForDatePicker = resolvedLanguage === 'de' ? 'de' : 'enGB'
    const datePattern = useRef(getDatePattern(languageForDatePattern))
    const [inViewRef, isFullyInView] = useInView({threshold: 1})

    useEffect(() => {
        if (typeof showOnlyCalendarIcon !== 'undefined' && !showOnlyCalendarIcon && typeof numberInputAriaErrorMessage === 'undefined') throw new Error('You must provide and aria error message for the number input component if you set showOnlyCalendar to false.')
    }, []);

    useEffect(() => {
        if (
            typeof validateForm !== 'undefined' &&
            validateForm > VALIDATE_FORM_STARTING_NUMBER &&
            _evaluateForm()
        ) {
            const convertedStartDate: DateTime = DateTime.fromJSDate(startDate)
            const convertedEndDate: DateTime = DateTime.fromJSDate(endDate ?? startDate)
            if (typeof onFormIsValid !== 'undefined') {
                if (useExactDates) {
                    onFormIsValid(useExactDates, count, {startDate: convertedStartDate, endDate: convertedEndDate})
                } else {
                    onFormIsValid(useExactDates, count)
                }
            }
        }
    }, [validateForm]);

    useEffect(() => {
        datePattern.current = getDatePattern(languageForDatePattern)
    }, [language])

    useEffect(() => {
        if (!openCalendar) {
            if (!(count >= minNumberNightsOrDays || count <= maxNumberNightsOrDays)) {
                setNumberOfNightsOrDaysError(true)
            } else {
                setNumberOfNightsOrDaysError(false)
            }
        }
    }, [openCalendar]);

    useOutsideClick(calendarRef, () => {
        setCalendarOpen && setCalendarOpen(false)
        setOpenCalendar(false)
    }, [iconButtonRef])

    const _evaluateForm = () => {
        const numberOfNightsOrDaysError = !(count >= minNumberNightsOrDays && count <= maxNumberNightsOrDays)
        setNumberOfNightsOrDaysError(numberOfNightsOrDaysError)
        return !numberOfNightsOrDaysError
    }

    const _handleNightCountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const {target: {valueAsNumber: newDuration}} = event
        setNumberOfNightsOrDaysError(false)
        const convertedStartDate = DateTime.fromJSDate(startDate)
        let newEndDate
        if (!isNights) {
            newEndDate = cloneDeep(convertedStartDate.plus({days: newDuration - 1}))
        } else {
            newEndDate = cloneDeep(convertedStartDate.plus({days: newDuration}))
        }
        const newRange = {startDate: convertedStartDate, endDate: newEndDate}
        setCount(newDuration)
        setEndDate(newEndDate.toJSDate())
        setNightsDaysCount && setNightsDaysCount(useExactDates, newDuration, newRange)
        setDateRange && useExactDates && setDateRange(newRange, newDuration)
    }

    const _handleRangePickerChange = (dates: [Date, Date | null]) => {
        const [start, end] = dates
        setStartDate(start)
        setEndDate(end)
        let convertedStartDate: DateTime = DateTime.fromJSDate(start)
        let convertedEndDate: DateTime = DateTime.fromJSDate(end ? end : start)
        const newDurationCount = getDaysCountBetweenDates(convertedStartDate, convertedEndDate)
        if (end) {
            setOpenCalendar(false)
            setCalendarOpen && setCalendarOpen(false)
        }
        if (newDurationCount <= maxNumberNightsOrDays) {
            const newRange = {startDate: convertedStartDate, endDate: convertedEndDate}
            let countDayNights = convertedEndDate.diff(convertedStartDate, ["days"]).days
            if (!isNights) countDayNights += 1
            setCount(countDayNights)
            setNightsDaysCount && setNightsDaysCount(useExactDates, countDayNights, newRange)
            setDateRange && setDateRange(newRange, countDayNights)
            setCalendarOpen && setCalendarOpen(false)
        } else {
            showMaxHotelDurationExceededToast && toast(<ToastWithTextOnly
                    message={t("TOASTS.ERROR.HOTEL_NIGHTS_EXCEED_JOURNEY_DAYS", {
                        days: maxNumberNightsOrDays,
                        nights: newDurationCount
                    })}/>,
                {progress: undefined, type: "error"}
            )
        }
    }

    const _disableCalendarIcon = () => {
        if (showOnlyCalendarIcon) return false
        return !useExactDates
    }

    const _renderDateRangePicker = () => <DatePicker
        selected={startDate}
        onChange={_handleRangePickerChange}
        openToDate={openToDate?.toJSDate() ?? undefined}
        minDate={minDate?.toJSDate() ?? undefined}
        maxDate={maxDate?.toJSDate() ?? undefined}
        startDate={startDate}
        endDate={endDate}
        dateFormat={setDateFormat(resolvedLanguage)}
        selectsRange={true}
        inline={true}
        disabled={disableCalendar || disabled}
        className={clsx("date-range-picker-calendar-element", isFullyInView && 'adjusted-positioning')}
        locale={localeForDatePicker}
    />

    const _renderCalendarIcon = () => <div className="calendarWrap">
        <div className={'date-range-picker-icon-date-wrapper'} ref={iconButtonRef}>
            <IconButton
                aria-label={"ARIA.CALENDAR"}
                disabled={disabled || disableCalendar || _disableCalendarIcon()}
                onClick={() => {
                    setCalendarOpen && setCalendarOpen(!openCalendar)
                    setOpenCalendar(!openCalendar)
                }}
                size={"medium"}
                icon={"calendarDays"}
            />
            {useExactDates && <div className={"date-range-picker-date-text-wrapper"}>
                <Text
                    variant={"subtitleSmall"}>{formatDateString(DateTime.fromJSDate(startDate), language, false)}</Text>
                <Text classes={'date-range-picker-dash'} variant={"subtitleSmall"}>—</Text>
                <Text
                    variant={"subtitleSmall"}>{formatDateString(DateTime.fromJSDate(endDate ?? startDate), language, false)}</Text>
            </div>}
        </div>
        <div ref={calendarRef}>
            <div ref={inViewRef} className={clsx(openCalendar && 'date-range-picker-in-view-wrapper')}>
                {openCalendar && _renderDateRangePicker()}
            </div>
        </div>
    </div>

    const _renderCheckbox = () => <CheckboxInput
        containerStyle={{height: "unset", minWidth: "unset"}}
        readOnly={disabled || disableCalendar}
        checked={useExactDates}
        onChange={() => {
            setUseExactDates(!useExactDates)
            const convertedStartDate: DateTime = DateTime.fromJSDate(startDate)
            const convertedEndDate: DateTime = DateTime.fromJSDate(endDate ?? startDate)
            const dateRange = {
                startDate: convertedStartDate,
                endDate: convertedEndDate
            }
            setUseDates && setUseDates(
                !useExactDates,
                {
                    startDate: convertedStartDate,
                    endDate: convertedEndDate
                },
                count)
            setNightsDaysCount && setNightsDaysCount(!useExactDates, count, dateRange)

        }}
        hideLabel={true}
        label={"GENERAL.DATE"}/>

    const _renderCalendarContent = () => <>
        {!showOnlyCalendarIcon && <Text variant={"label"}>GENERAL.DATE</Text>}
        <div className={"date-range-picker-checkbox-icon-label-wrapper"}>
            {!showOnlyCalendarIcon &&
                <>
                    {_renderCheckbox()}
                </>
            }
            {disableCalendar && disableCalendarTooltip ? <Tooltip
                placement={"top"}
                title={t(disableCalendarTooltip)}>
                <div className={'date-range-picker-icon-date-wrapper'}>{_renderCalendarIcon()}</div>
            </Tooltip> : _renderCalendarIcon()}
        </div>
    </>

    const _renderContent = () => {
        if (showOnlyCalendar) return _renderDateRangePicker()
        return <>
            {!showOnlyCalendarIcon &&
                <NumberInput
                    readOnly={disabled}
                    min={minNumberNightsOrDays}
                    max={maxNumberNightsOrDays}
                    error={numberOfNightsOrDaysError}
                    aria-errormessage={numberInputAriaErrorMessage && t(numberInputAriaErrorMessage, {
                        min: minNumberNightsOrDays,
                        max: maxNumberNightsOrDays
                    })}
                    injectMinimum={true}
                    injectMaximum={true}
                    required={true}
                    label={isNights ? "LABELS.COMMON.NUMBER_NIGHTS" : "LABELS.COMMON.NUMBER_DAYS"}
                    onBlur={event => {
                        const {target: {valueAsNumber}} = event
                        setCount(valueAsNumber)
                        const convertedStartDate: DateTime = DateTime.fromJSDate(startDate)
                        const convertedEndDate: DateTime = DateTime.fromJSDate(endDate ?? startDate)
                        setNightsDaysCount && setNightsDaysCount(useExactDates, valueAsNumber, {
                            startDate: convertedStartDate, endDate: convertedEndDate
                        })
                    }}
                    onChange={_handleNightCountChange}
                    containerClasses={'date-range-picker-number-input'}
                    containerStyle={numberInputContainerStyle}
                    value={count}/>
            }
            <div
                style={{justifyContent: showOnlyCalendarIcon ? "flex-start" : 'center'}}
                className={clsx("date-range-picker-checkbox-icon-wrapper", showOnlyCalendarIcon && "date-range-picker-overwrite-number-checkbox-input-container", numberCheckboxWrapperClasses)}>
                {_renderCalendarContent()}
            </div>
        </>
    }

    return <Fieldset
        fieldsetStyles={containerStyles}
        legend={isNights ? "LABELS.COMMON.NUMBER_NIGHTS" : "LABELS.COMMON.NUMBER_DAYS"}
        showLegend={false}
        fieldsetClasses={containerClasses}>
        {_renderContent()}
    </Fieldset>
}


export default DateRangePicker